import React from "react";

// Writer-facing summary only. Reuses existing saved-source readiness; no API calls,
// print approval, snapshot capture, page-count claims, publishing or export.
const clean = (value) => String(value ?? "").trim();
const rows = (value) => Array.isArray(value) ? value : [];

export function novelWriterReadinessV3C3({ readiness = {}, sourceManifest = {}, printPlanning = {}, previewOnly = false, storyId = "" } = {}) {
  const checks = rows(readiness.checks);
  const check = (id) => checks.find((item) => item.id === id)?.ready === true;
  const manuscriptReady = check("manuscript") && check("unassigned") && sourceManifest.readyForProductionContract === true;
  const formatReady = check("format") && check("front-cover") && check("back-cover") &&
    (printPlanning.digitalOnly === true || printPlanning.settingsComplete === true);
  const previewAvailable = Boolean(clean(storyId)) && !previewOnly;
  const pendingManuscript = rows(sourceManifest.issues).length;
  return { manuscriptReady, formatReady, previewAvailable, pendingManuscript,
    readyToPreview: manuscriptReady && formatReady && previewAvailable,
    pendingChecks: checks.filter((item) => !item.ready).length };
}

export default function NovelPublicationSimpleOverviewV3C3({
  title = "", authorName = "", storyId = "", previewOnly = false,
  frontMatter = [], chapters = [], backMatter = [], readiness = {}, sourceManifest = {},
  printPlanning = {}, onOpenReview, onOpenBookSetup, onOpenProof, isFR = false,
}) {
  const s = novelWriterReadinessV3C3({ readiness, sourceManifest, printPlanning, storyId, previewOnly });
  const label = (en, fr) => isFR ? fr : en;
  const steps = [
    {
      id: "manuscript", number: "1", title: label("Review Your Manuscript", "Relire votre manuscrit"),
      status: s.manuscriptReady ? label("Ready for preview", "Prêt pour l'aperçu") : label("Needs attention", "À vérifier"),
      detail: s.manuscriptReady
        ? label("All saved sections and assigned Canon Scenes are accounted for.", "Toutes les sections enregistrées et scènes Canon sont prises en compte.")
        : label("Check your saved Chapters, assigned Scenes and missing book sections.", "Vérifiez vos chapitres, scènes affectées et sections manquantes."),
      action: label("Review Book", "Relire le livre"), onClick: onOpenReview, ready: s.manuscriptReady,
    },
    {
      id: "format", number: "2", title: label("Choose Your Book Format", "Choisir le format du livre"),
      status: s.formatReady ? label("Set for preview", "Défini pour l'aperçu") : label("Needs attention", "À compléter"),
      detail: s.formatReady
        ? label("Saved format and required cover choices are present.", "Le format et les couvertures nécessaires sont enregistrés.")
        : label("Complete your format and cover choices in Book Setup.", "Complétez les choix de format et de couverture."),
      action: label("Book Setup", "Préparer le livre"), onClick: onOpenBookSetup, ready: s.formatReady,
    },
    {
      id: "preview", number: "3", title: label("Check Book Layout", "Vérifier la mise en page"),
      status: s.previewAvailable ? label("Available", "Disponible") : label("Save your book first", "Enregistrez d'abord le livre"),
      detail: label("View the browser layout preview. These pages are provisional, not the final print PDF.", "Consultez la mise en page dans le navigateur. Ces pages sont provisoires, pas le PDF final pour l’impression."),
      action: label("Open Book Layout Preview", "Ouvrir l’aperçu de mise en page"), onClick: onOpenProof,
      ready: s.previewAvailable, disabled: !s.previewAvailable,
    },
    {
      id: "publish", number: "4", title: label("Approve Final Interior PDF", "Approuver le PDF intérieur final"),
      status: label("Not yet available", "Pas encore disponible"),
      detail: label("Final Interior Approval is a separate, restricted step. It does not approve a cover or publish the book.", "L’approbation de l’intérieur final est une étape distincte et réservée. Elle ne valide pas la couverture et ne publie pas le livre."),
      ready: false, disabled: true,
    },
  ];

  return (
    <article className="ns-card" data-novel-writer-publication-v3c3="simple"
      style={{ padding: 22, borderRadius: 18,
        background: "linear-gradient(145deg, #f0faf4 0%, #ffffff 46%)",
        border: "1px solid #c6e5d4", borderLeft: "5px solid #047857",
        boxShadow: "0 5px 18px rgba(15, 118, 110, 0.06)" }}>
      <p style={{ margin: "0 0 5px", color: "#047857", fontSize: 12,
        fontWeight: 850, letterSpacing: "0.09em" }}>
        {label("YOUR PUBLICATION JOURNEY", "VOTRE PARCOURS DE PUBLICATION")}
      </p>
      <h3 style={{ margin: "0 0 9px", color: "#173d32", fontSize: 23 }}>
        {label("Your Book Readiness", "Préparation de votre livre")}
      </h3>
      <p style={{ margin: "0 0 8px", fontWeight: 700 }}>{clean(title)}
        {clean(authorName) ? <span className="ns-card-meta">{" · "}{clean(authorName)}</span> : null}
      </p>
      <p className="ns-card-meta" style={{ margin: "0 0 14px" }}>
        {rows(frontMatter).length} {label("Front Matter", "Préliminaires")}
        {" · "}{rows(chapters).length} {label("Chapters", "Chapitres")}
        {" · "}{rows(backMatter).length} {label("Back Matter", "Postliminaires")}
        {" · "}{sourceManifest.sceneCount || 0} {label("Canon Scenes", "Scènes Canon")}
      </p>
      <p data-novel-writer-readiness-status={s.readyToPreview ? "ready-for-preview" : "needs-attention"}
        style={{ margin: "0 0 14px", fontWeight: 750, color: s.readyToPreview ? "#047857" : "#92400e" }}>
        {s.readyToPreview
          ? label("Your saved book is ready for a private preview—not yet approved for printing.", "Votre livre est prêt pour l'aperçu privé, mais pas encore pour l'impression.")
          : label("You can preview your saved book while completing the items that need attention.", "Vous pouvez prévisualiser votre livre tout en complétant les éléments manquants.")}
      </p>
      <div style={{ display: "grid", gap: 10 }}>
        {steps.map((step) => (
          <div key={step.id} data-novel-writer-step={step.id}
            style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap",
              padding: "14px 15px", background: step.ready ? "#ffffff" : "#fffdf8",
              border: "1px solid #dce8e2", borderRadius: 12,
              borderLeft: step.ready ? "4px solid #059669" : "4px solid #d6a44a" }}>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flex: "1 1 280px" }}>
              <span aria-hidden="true" style={{ display: "inline-grid", placeItems: "center",
                width: 30, height: 30, flex: "0 0 30px", borderRadius: 9, fontWeight: 850,
                background: step.ready ? "#d1fae5" : "#fef3c7",
                color: step.ready ? "#065f46" : "#92400e" }}>{step.number}</span>
              <div style={{ minWidth: 0 }}>
                <strong>{step.title}</strong>
              <p className="ns-card-meta" style={{ margin: "5px 0 3px" }}>{step.detail}</p>
                <span style={{ fontSize: 12, fontWeight: 750,
                  color: step.ready ? "#047857" : "#92400e" }}>{step.status}</span>
              </div>
            </div>
            {step.onClick ? (
              <button type="button" className="ns-button ns-button-outline"
                disabled={step.disabled === true || typeof step.onClick !== "function"}
                onClick={step.onClick}>{step.action}</button>
            ) : null}
          </div>
        ))}
      </div>
      <p data-novel-writer-return-guidance-v3c3b="true" className="ns-card-meta"
        style={{ margin: "14px 0 0", color: "#365b4b" }}>
        {label("Opened a step? Use ‘Back to Publication Preparation’ on that page to return here.",
          "Vous avez ouvert une étape ? Utilisez « Retour à la préparation » sur cette page pour revenir ici.")}
      </p>
      <p data-novel-writer-publishing-gate="disabled" className="ns-card-meta"
        style={{ margin: "14px 0 0" }}>
        {label("This preparation checklist does not approve a final PDF, lock the manuscript or publish the book. Final interior approval is a separate controlled step.",
          "Cette liste de préparation ne valide pas de PDF final, ne verrouille pas le manuscrit et ne publie pas le livre. L’approbation finale est une étape contrôlée séparément.")}
      </p>
    </article>
  );
}
